// src\services\RealTimeService.js

/**
 * Service de synchronisation des données en temps réel
 * Dans une application réelle, ce service utiliserait WebSockets ou Server-Sent Events
 */
class RealTimeService {
    constructor() {
      this.callbacks = {
        'batch-update': [],
        'order-update': [],
        'resource-update': [],
        'alert': []
      };
      this.connected = false;
      this.reconnectAttempts = 0;
      this.maxReconnectAttempts = 5;
      this.reconnectDelay = 2000; // 2 secondes
      this.simulatedUpdatesInterval = null;
    }
  
    /**
     * Se connecter au service de temps réel
     * @param {string} url - URL du service
     * @returns {Promise<boolean>} - Statut de la connexion
     */
    connect(url = 'wss://api.example.com/realtime') {
      return new Promise((resolve) => {
        console.log(`Tentative de connexion au service temps réel: ${url}`);
        
        // Simulation d'une connexion réussie après un délai
        setTimeout(() => {
          this.connected = true;
          console.log('Connecté au service temps réel');
          
          // Démarrer la simulation des mises à jour
          this.startSimulatedUpdates();
          
          resolve(true);
        }, 1000);
      });
    }
  
    /**
     * Se déconnecter du service
     */
    disconnect() {
      this.connected = false;
      
      // Arrêter la simulation des mises à jour
      if (this.simulatedUpdatesInterval) {
        clearInterval(this.simulatedUpdatesInterval);
        this.simulatedUpdatesInterval = null;
      }
      
      console.log('Déconnecté du service temps réel');
    }
  
    /**
     * S'abonner à un type d'événement
     * @param {string} eventType - Type d'événement
     * @param {Function} callback - Fonction de rappel
     * @returns {Function} - Fonction pour se désabonner
     */
    subscribe(eventType, callback) {
      if (!this.callbacks[eventType]) {
        this.callbacks[eventType] = [];
      }
      
      this.callbacks[eventType].push(callback);
      
      // Retourner une fonction pour se désabonner
      return () => {
        this.callbacks[eventType] = this.callbacks[eventType].filter(cb => cb !== callback);
      };
    }
  
    /**
     * Émettre un événement à tous les abonnés
     * @param {string} eventType - Type d'événement
     * @param {any} data - Données de l'événement
     */
    emit(eventType, data) {
      if (!this.callbacks[eventType]) return;
      
      this.callbacks[eventType].forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error(`Erreur lors de l'exécution du callback pour ${eventType}:`, error);
        }
      });
    }
  
    /**
     * Démarrer la simulation des mises à jour
     * Pour une démonstration, génère des événements aléatoires
     * @private
     */
    startSimulatedUpdates() {
      if (this.simulatedUpdatesInterval) {
        clearInterval(this.simulatedUpdatesInterval);
      }
      
      this.simulatedUpdatesInterval = setInterval(() => {
        // Générer un type d'événement aléatoire
        const eventTypes = ['batch-update', 'order-update', 'resource-update', 'alert'];
        const randomEventType = eventTypes[Math.floor(Math.random() * eventTypes.length)];
        
        // Générer des données simulées selon le type d'événement
        let simulatedData;
        
        switch (randomEventType) {
          case 'batch-update':
            simulatedData = {
              batchNumber: `B${Math.floor(Math.random() * 10000).toString().padStart(4, '0')}`,
              status: ['scheduled', 'in-progress', 'completed'][Math.floor(Math.random() * 3)],
              completedItems: Math.floor(Math.random() * 5),
              totalItems: 5 + Math.floor(Math.random() * 5)
            };
            break;
            
          case 'order-update':
            simulatedData = {
              id: `OF-${Math.floor(Math.random() * 10000)}`,
              status: ['waiting', 'in-progress', 'completed'][Math.floor(Math.random() * 3)],
              alert: Math.random() > 0.8 ? 'Problème détecté' : null
            };
            break;
            
          case 'resource-update':
            simulatedData = {
              id: Math.floor(Math.random() * 4) + 1,
              status: ['available', 'maintenance', 'in-use'][Math.floor(Math.random() * 3)]
            };
            break;
            
          case 'alert':
            simulatedData = {
              type: ['info', 'warning', 'error'][Math.floor(Math.random() * 3)],
              message: [
                'Maintenance programmée', 
                'Retard de production détecté', 
                'Problème technique sur un moyen chaud'
              ][Math.floor(Math.random() * 3)]
            };
            break;
        }
        
        // Émettre l'événement simulé
        this.emit(randomEventType, simulatedData);
        
      }, 30000); // Toutes les 30 secondes
    }
  
    /**
     * Gérer une déconnexion imprévue
     * @private
     */
    handleDisconnect() {
      this.connected = false;
      
      if (this.reconnectAttempts < this.maxReconnectAttempts) {
        this.reconnectAttempts++;
        
        console.log(`Tentative de reconnexion ${this.reconnectAttempts}/${this.maxReconnectAttempts} dans ${this.reconnectDelay / 1000}s...`);
        
        setTimeout(() => {
          this.connect().catch(() => {
            this.handleDisconnect();
          });
        }, this.reconnectDelay);
        
        // Augmenter le délai pour la prochaine tentative
        this.reconnectDelay = Math.min(this.reconnectDelay * 1.5, 30000); // Max 30 secondes
      } else {
        console.error('Nombre maximum de tentatives de reconnexion atteint');
        
        // Émettre une alerte
        this.emit('alert', {
          type: 'error',
          message: 'La connexion au service de mise à jour en temps réel a été perdue. Veuillez actualiser la page.'
        });
      }
    }
  }
  
  // Exporter une instance singleton
  export default new RealTimeService();